import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAnalytics } from '../contexts/AnalyticsContext';

const { FiCopy, FiCheck, FiCpu, FiUserCheck, FiZap, FiDatabase, FiShield, FiFileText, FiCode, FiActivity, FiLayers, FiAlertTriangle } = FiIcons;

const TrackingSetup = () => {
  const { trackingId } = useAnalytics();
  const [copied, setCopied] = useState(null);
  const [origin, setOrigin] = useState('https://intuitus.io');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setOrigin(window.location.origin);
    }
  }, []);

  const snippets = {
    header: `<!-- Intuitus Core - Add to <head> -->
<script>
  window.intuitus_user_serial = "USER_IDENTIFIER"; // Map to Email/ID from your Auth system
</script>
<script src="${origin}/analytics-tracking.js" async></script>`,
    identify: `// Manual Identity Resolution
window.intuitus.identify('john.doe@example.com', {
  membership: 'platinum',
  company: 'Global Industries'
});`,
    event: `// Custom Behavioral Tracking
window.intuitus.track('Checkout Completed', {
  cart_value: 1250.00,
  item_count: 3,
  coupon_used: 'SPRING24'
});`
  };

  const copy = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="space-y-8">
      {/* Update Alert */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex gap-4 items-start shadow-sm">
        <div className="text-amber-600 mt-1">
          <SafeIcon icon={FiAlertTriangle} className="text-2xl" />
        </div>
        <div>
          <h3 className="text-amber-900 font-bold text-lg tracking-tight">Script Update Required</h3>
          <p className="text-amber-700 text-sm mt-1 mb-3">
            The telemetry engine has been upgraded to support direct cross-origin database ingestion. If you have previously hardcoded the tracking script onto your website, you must update the script tag below to ensure data reaches the dashboard.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-12">
        <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-blue-600 text-white">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Implementation Hub</h1>
            <p className="text-blue-100 font-medium mt-1 text-sm">Infrastructure setup for Intuitus v2.0 (Live Ingest)</p>
          </div>
          <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm border border-white/20">
            <SafeIcon icon={FiCpu} className="text-3xl" />
          </div>
        </div>

        <div className="p-8 space-y-12">
          {/* Main Script */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                <SafeIcon icon={FiCode} className="text-lg" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Neural Tracker Deployment</h2>
            </div>
            <p className="text-slate-600 mb-6 text-sm">
              Deploy the core tracker to enable real-time telemetry. Copy and paste this snippet exactly as below into the <code className="bg-slate-100 px-1 py-0.5 rounded text-slate-800 text-xs font-mono">&lt;head&gt;</code> of your website.
            </p>
            <CodeBlock code={snippets.header} onCopy={() => copy('header', snippets.header)} isCopied={copied === 'header'} />
          </section>

          {/* Advanced Triggers */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
              <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                <SafeIcon icon={FiUserCheck} className="text-blue-600" /> Identity Stitching
              </h3>
              <p className="text-xs text-slate-500 mb-4">
                Call the identify method during authentication flows. This stitches the anonymous visitor ID to your permanent user record.
              </p>
              <CodeBlock code={snippets.identify} onCopy={() => copy('identify', snippets.identify)} isCopied={copied === 'identify'} />
            </div>
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
              <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                <SafeIcon icon={FiActivity} className="text-blue-600" /> Custom Signals
              </h3>
              <p className="text-xs text-slate-500 mb-4">
                Track business-specific conversion signals. High-fidelity data is automatically sanitized and sent to the Intuitus Neural Engine.
              </p>
              <CodeBlock code={snippets.event} onCopy={() => copy('event', snippets.event)} isCopied={copied === 'event'} />
            </div>
          </section>

          {/* Technical Reference Guide */}
          <section className="pt-8 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center text-white">
                <SafeIcon icon={FiLayers} className="text-lg" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Technical Reference</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <SafeIcon icon={FiDatabase} className="text-xl text-emerald-500 mb-3" />
                <h4 className="font-bold text-slate-900 text-sm mb-3">Direct Ingest Pipeline</h4>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Data is now securely transmitted directly to your Supabase tables via REST API utilizing Row Level Security (RLS) policies, eliminating CORS and local persistence limitations.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <SafeIcon icon={FiShield} className="text-xl text-blue-600 mb-3" />
                <h4 className="font-bold text-slate-900 text-sm mb-3">Data Governance</h4>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Intuitus operates on a <strong>zero-trust</strong> data model. All payloads are encrypted via SSL. PII is handled according to configuration settings before persistence.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <SafeIcon icon={FiFileText} className="text-xl text-blue-600 mb-3" />
                <h4 className="font-bold text-slate-900 text-sm mb-3">Payload Spec</h4>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Format: <code className="text-blue-600 bg-blue-50 px-1 py-0.5 rounded">JSON</code><br/>
                  Encoding: <code className="text-blue-600 bg-blue-50 px-1 py-0.5 rounded">UTF-8</code><br/>
                  Engine captures: Timestamp, Page Path, Viewport, Device Type, and Referrer data automatically.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

const CodeBlock = ({ code, onCopy, isCopied }) => (
  <div className="relative group">
    <div className="relative bg-slate-900 rounded-xl p-4 overflow-hidden shadow-inner border border-slate-800">
      <pre className="text-blue-300 font-mono text-xs leading-relaxed overflow-x-auto">
        <code>{code}</code>
      </pre>
      <button 
        onClick={onCopy} 
        className="absolute top-3 right-3 bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-widest transition-all backdrop-blur-sm border border-white/10"
      >
        <div className="flex items-center gap-1.5">
          <SafeIcon icon={isCopied ? FiCheck : FiCopy} />
          {isCopied ? 'Copied' : 'Copy'}
        </div>
      </button>
    </div>
  </div>
);

export default TrackingSetup;